import type { ApiEnvelope, ApiProblem } from "@nutrayan/api-types";

export interface ApiClientConfig {
  baseUrl: string;
  tenantCode?: string;
  getAuthToken?: () => string | undefined | Promise<string | undefined>;
  getCartToken?: () => string | undefined;
  getRequestId?: () => string;
  defaultLocale?: string;
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ApiProblem;
  readonly traceId?: string;

  constructor(status: number, problem: ApiProblem) {
    super(problem.title ?? problem.detail ?? `Request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
    if (problem.traceId !== undefined) this.traceId = problem.traceId;
  }
}

export class NetworkError extends Error {
  override readonly cause?: unknown;

  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "NetworkError";
    if (cause !== undefined) this.cause = cause;
  }
}

function defaultRequestId(): string {
  // Avoid crypto.randomUUID dependence for Edge runtimes that may polyfill differently.
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  idempotencyKey?: string;
  locale?: string;
  /** When true, return the raw response instead of unwrapping `{data, meta}`. */
  raw?: boolean;
}

export function createApiClient(config: ApiClientConfig) {
  const {
    baseUrl,
    tenantCode = "nutrayan",
    getAuthToken,
    getCartToken,
    getRequestId = defaultRequestId,
    defaultLocale = "en-IN",
  } = config;

  async function request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const { body, idempotencyKey, locale, raw, headers, ...rest } = options;

    const finalHeaders = new Headers(headers ?? {});
    finalHeaders.set("Accept", "application/json");
    finalHeaders.set("Accept-Language", locale ?? defaultLocale);
    finalHeaders.set("X-Tenant-Code", tenantCode);
    finalHeaders.set("X-Request-ID", getRequestId());

    if (body !== undefined && !finalHeaders.has("Content-Type")) {
      finalHeaders.set("Content-Type", "application/json");
    }

    const token = getAuthToken ? await getAuthToken() : undefined;
    if (token) finalHeaders.set("Authorization", `Bearer ${token}`);

    const cartToken = getCartToken?.();
    if (cartToken) finalHeaders.set("Cart-Token", cartToken);

    if (idempotencyKey) finalHeaders.set("Idempotency-Key", idempotencyKey);

    const init: RequestInit = {
      ...rest,
      headers: finalHeaders,
    };
    if (body !== undefined) init.body = JSON.stringify(body);

    let res: Response;
    try {
      res = await fetch(joinUrl(baseUrl, path), init);
    } catch (cause) {
      throw new NetworkError("Network request failed", cause);
    }

    const contentType = res.headers.get("Content-Type") ?? "";
    const isJson = contentType.includes("application/json") || contentType.includes("problem+json");
    const payload: unknown =
      isJson && res.status !== 204 ? await res.json().catch(() => null) : null;

    if (!res.ok) {
      const problem: ApiProblem = isProblem(payload) ? payload : { status: res.status };
      throw new ApiError(res.status, problem);
    }

    if (raw) return payload as T;
    return unwrap<T>(payload);
  }

  return { request };
}

function joinUrl(base: string, path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  const sep = base.endsWith("/") || path.startsWith("/") ? "" : "/";
  return `${base}${sep}${path}`;
}

function isProblem(value: unknown): value is ApiProblem {
  return typeof value === "object" && value !== null;
}

function unwrap<T>(payload: unknown): T {
  if (payload && typeof payload === "object" && "data" in (payload as object)) {
    return (payload as ApiEnvelope<T>).data;
  }
  return payload as T;
}
